import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { BookmarkSimple, Plus, Trash, ArrowsCounterClockwise, Copy, MagicWand, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Campaign Template Library — list, spawn (1-click duplicate), delete.
 *
 * Templates live in `email_campaign_templates` collection, scoped per profile.
 * "Spawn" creates a new draft sequence-campaign pre-filled with the template's
 * product + 6-step sequence + segment filter.
 */
export default function CampaignTemplatesList({ apiBase, onSpawn, refreshKey }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spawning, setSpawning] = useState(null);
  const [spawnDialog, setSpawnDialog] = useState(null);
  const [spawnName, setSpawnName] = useState('');
  // Create new template flow
  const [createDialog, setCreateDialog] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplProduct, setTplProduct] = useState('');
  const [tplProductDesc, setTplProductDesc] = useState('');
  const [tplTargetDesc, setTplTargetDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/campaign-templates`, { credentials: 'include' });
      const d = await r.json();
      setTemplates(d.templates || []);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [apiBase, refreshKey]);

  const spawn = async () => {
    if (!spawnDialog) return;
    setSpawning(spawnDialog.template_id);
    try {
      const r = await fetch(`${apiBase}/campaign-templates/${spawnDialog.template_id}/spawn`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: spawnName.trim() || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Spawn mislukt');
      toast.success(`Campagne "${d.name}" aangemaakt vanuit template`);
      setSpawnDialog(null);
      setSpawnName('');
      onSpawn && onSpawn(d.campaign_id);
      load();
    } catch (e) {
      toast.error(e.message || 'Spawn mislukt');
    }
    setSpawning(null);
  };

  const removeTemplate = async (template_id) => {
    if (!window.confirm('Template verwijderen?')) return;
    await fetch(`${apiBase}/campaign-templates/${template_id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Template verwijderd');
    load();
  };

  const createTemplate = async () => {
    if (!tplName.trim() || !tplProduct.trim()) {
      toast.error('Naam en product zijn verplicht');
      return;
    }
    setCreating(true);
    try {
      const r = await fetch(`${apiBase}/campaign-templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: tplName.trim(),
          product: tplProduct.trim(),
          product_description: tplProductDesc.trim(),
          target_description: tplTargetDesc.trim(),
          generate_ai: true,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Aanmaken mislukt');
      const stepCount = (d.sequence_steps || []).length;
      toast.success(`Template "${d.name}" aangemaakt${stepCount ? ` met ${stepCount} AI-stappen` : ''}`);
      setCreateDialog(false);
      setTplName(''); setTplProduct(''); setTplProductDesc(''); setTplTargetDesc('');
      load();
    } catch (e) {
      toast.error(e.message || 'Aanmaken mislukt');
    }
    setCreating(false);
  };

  return (
    <div className="space-y-4" data-testid="campaign-templates-list">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
            <BookmarkSimple size={14} className="text-emerald-600" /> Campagne Templates
          </h3>
          <p className="text-xs text-zinc-500">
            Hergebruikbare blueprints: product + 6-staps sequence + doelgroep filter
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="template-new-btn">
            <MagicWand size={12} className="mr-1.5" /> Nieuwe template (AI)
          </Button>
          <Button size="sm" variant="outline" onClick={load} data-testid="templates-reload-btn">
            <ArrowsCounterClockwise size={12} className="mr-1.5" /> Verversen
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-400 italic">Templates laden...</div>
      ) : templates.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-300 rounded-md p-6 text-center">
          Nog geen templates. Open een sequence-campagne via het Lightning-icoon, vul product en
          stappen in, en klik in de modal op "Opslaan als template".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t) => (
            <div key={t.template_id} className="border border-zinc-200 rounded-md p-3 bg-white space-y-2" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate" title={t.name}>{t.name}</div>
                  {t.product && (
                    <div className="text-xs text-zinc-500 truncate" title={t.product}>Product: {t.product}</div>
                  )}
                </div>
                <Badge className="rounded-sm px-1.5 py-0.5 text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {(t.sequence_steps || []).length} stappen
                </Badge>
              </div>

              {t.description && (
                <p className="text-xs text-zinc-600 line-clamp-2">{t.description}</p>
              )}

              <div className="flex items-center gap-3 text-[10px] text-zinc-400">
                <span>Gebruikt: {t.use_count || 0}×</span>
                {t.last_used_at && <span>laatst: {new Date(t.last_used_at).toLocaleDateString('nl-NL')}</span>}
              </div>

              <div className="flex gap-1.5 pt-1">
                <Button
                  size="sm"
                  onClick={() => { setSpawnDialog(t); setSpawnName(`${t.name} — ${new Date().toLocaleDateString('nl-NL')}`); }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={spawning === t.template_id}
                  data-testid={`template-spawn-${t.template_id}`}
                >
                  <Copy size={12} className="mr-1.5" /> Maak campagne
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeTemplate(t.template_id)}
                  className="text-red-600 hover:bg-red-50"
                  data-testid={`template-delete-${t.template_id}`}
                >
                  <Trash size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create new template dialog */}
      {createDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="template-create-dialog">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <MagicWand size={16} className="text-purple-600" /> Nieuwe template via AI
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Beschrijf product + doelgroep. AI maakt direct een 6-staps email-sequence die je later kunt finetunen per campagne.
                </p>
              </div>
              <button onClick={() => setCreateDialog(false)} className="p-1.5 hover:bg-zinc-100 rounded">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Templatenaam <span className="text-red-500">*</span></Label>
                <Input
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  placeholder='Bijv. "Betonverf outdoor 6-stappen"'
                  data-testid="tpl-create-name"
                />
              </div>
              <div>
                <Label className="text-xs">Product <span className="text-red-500">*</span></Label>
                <Input
                  value={tplProduct}
                  onChange={(e) => setTplProduct(e.target.value)}
                  placeholder='Bijv. "Betonverf voor outdoor zwembaden"'
                  data-testid="tpl-create-product"
                />
              </div>
              <div>
                <Label className="text-xs">Productbeschrijving</Label>
                <Textarea
                  value={tplProductDesc}
                  onChange={(e) => setTplProductDesc(e.target.value)}
                  rows={3}
                  placeholder="USPs, voordelen, technische specs, prijs..."
                  data-testid="tpl-create-product-desc"
                />
              </div>
              <div>
                <Label className="text-xs">Doelgroepomschrijving</Label>
                <Textarea
                  value={tplTargetDesc}
                  onChange={(e) => setTplTargetDesc(e.target.value)}
                  rows={2}
                  placeholder='Bijv. "Eigenaars van betonnen outdoor zwembaden in NL/BE die renoveren"'
                  data-testid="tpl-create-target-desc"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1 border-t">
              <Button variant="outline" onClick={() => setCreateDialog(false)} data-testid="tpl-create-cancel">Annuleren</Button>
              <Button
                onClick={createTemplate}
                disabled={creating || !tplName.trim() || !tplProduct.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="tpl-create-confirm"
              >
                <MagicWand size={12} className="mr-1.5" />
                {creating ? 'AI bezig (~10s)...' : 'Genereer template'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Spawn dialog */}
      {spawnDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5 space-y-4" data-testid="template-spawn-dialog">
            <div>
              <h3 className="text-base font-semibold">Campagne maken uit template</h3>
              <p className="text-xs text-zinc-500 mt-1">Template: <strong>{spawnDialog.name}</strong></p>
            </div>
            <div>
              <label className="text-xs text-zinc-600 block mb-1">Naam nieuwe campagne</label>
              <Input
                value={spawnName}
                onChange={(e) => setSpawnName(e.target.value)}
                placeholder="Naam"
                autoFocus
                data-testid="template-spawn-name-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setSpawnDialog(null)} data-testid="template-spawn-cancel">Annuleren</Button>
              <Button
                onClick={spawn}
                disabled={spawning === spawnDialog.template_id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="template-spawn-confirm"
              >
                <Plus size={12} className="mr-1.5" /> Maak draft campagne
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
